import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import './TradesPage.css';
import AdminLayout from '../AdminLayout';
import {
  useGetAdminTradesQuery,
  useAdminCompleteCardInTradeMutation,
  AdminTrade,
  AdminTradeItem,
} from 'store/services/trade/tradeApi';
import { Points } from 'components/Points/Points';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import AdminTradeExpandedRow from './components/AdminTradeExpandedRow';
import AdminEditTradeItemModal from './components/AdminEditTradeItemModal';
import AdminRemoveCardConfirmModal from './components/AdminRemoveCardConfirmModal';

const TradesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page] = useState(1);
  const [expandedTrades, setExpandedTrades] = useState<Set<string>>(new Set());
  const [editingItem, setEditingItem] = useState<{ trade: AdminTrade; item: AdminTradeItem } | null>(null);
  const [removingItem, setRemovingItem] = useState<{ trade: AdminTrade; item: AdminTradeItem } | null>(null);
  const [completingItemId, setCompletingItemId] = useState<string | null>(null);
  const [completeCard] = useAdminCompleteCardInTradeMutation();

  const { data, isLoading, error } = useGetAdminTradesQuery({
    page,
    pageSize: 50,
    search: searchTerm,
    status: statusFilter as any || undefined,
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}.${day}.${year}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#10b981';
      case 'transit': return '#f59e0b';
      case 'ready': return '#3b82f6';
      case 'cancelled': return '#ef4444';
      default: return '#999';
    }
  };

  const formatStatus = (status: string) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'transit': return 'In Transit';
      case 'ready': return 'Ready to Ship';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const renderAvatar = (username: string, avatarUrl?: string, countryCode?: string) => (
    <AvatarWithFlag avatarUrl={avatarUrl} username={username} countryCode={countryCode} size={32} />
  );

  const toggleExpand = useCallback((tradeId: string) => {
    setExpandedTrades(prev => {
      const next = new Set(prev);
      if (next.has(tradeId)) {
        next.delete(tradeId);
      } else {
        next.add(tradeId);
      }
      return next;
    });
  }, []);

  const handleEditItem = useCallback((trade: AdminTrade, item: AdminTradeItem) => {
    setEditingItem({ trade, item });
  }, []);

  const handleRemoveItem = useCallback((trade: AdminTrade, item: AdminTradeItem) => {
    setRemovingItem({ trade, item });
  }, []);

  // One-click per-card complete (credits the sender). Server enforces mutual exclusivity,
  // and the ['ActiveTrades'] invalidation refetches the list so the row flips to "Completed".
  const handleCompleteItem = useCallback(async (trade: AdminTrade, item: AdminTradeItem) => {
    if (!item.id) return;
    setCompletingItemId(item.id);
    try {
      const res = await completeCard({ tradeId: trade.id, itemId: item.id }).unwrap();
      const credited = res.creditedPoints ?? item.pointValue;
      toast.success(`${item.name} completed. ${credited} points credited to sender.`);
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to complete card. Please try again.');
    } finally {
      setCompletingItemId(null);
    }
  }, [completeCard]);

  const trades = data?.trades || [];

  if (isLoading) {
    return (
      <AdminLayout title="Trades" currentPath="/admin/trades">
        <div className="trades-page">
          <div className="trades-header">
            <h1 className="trades-title">Trades</h1>
          </div>
          <div className="trades-table-container">
            <div className="trades-empty">Loading trades...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Trades" currentPath="/admin/trades">
        <div className="trades-page">
          <div className="trades-header">
            <h1 className="trades-title">Trades</h1>
          </div>
          <div className="trades-table-container">
            <div className="trades-empty">Error loading trades. Please try again later.</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Trades" currentPath="/admin/trades">
      <div className="trades-page trades-page-full-width">
        <div className="trades-header">
          <h1 className="trades-title">Trades</h1>

          <div className="trades-controls">
            <input
              type="text"
              className="trades-search-input"
              placeholder="Search by username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="trades-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="ready">Ready to Ship</option>
              <option value="transit">In Transit</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div className="trades-table-container">
          <table className="trades-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Trade #</th>
                <th>Sender</th>
                <th>Value</th>
                <th>Receiver</th>
                <th>Initiated At</th>
                <th>Shipped At</th>
                <th>Completed At</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {trades.length === 0 ? (
                <tr>
                  <td colSpan={9} className="trades-empty">
                    {searchTerm ? `No trades found for "${searchTerm}"` : 'No trades available'}
                  </td>
                </tr>
              ) : (
                trades.map((trade) => {
                  const isExpanded = expandedTrades.has(trade.id);
                  return (
                    <React.Fragment key={trade.id}>
                      <tr
                        className={`trades-row-clickable ${isExpanded ? 'trades-row-expanded' : ''}`}
                        onClick={() => toggleExpand(trade.id)}
                      >
                        <td className="trades-chevron-cell">
                          <span className={`trades-chevron ${isExpanded ? 'trades-chevron-open' : ''}`}>
                            &#9654;
                          </span>
                        </td>
                        <td className="trades-trade-number">{trade.tradeNumber}</td>
                        <td>
                          <div className="trades-user">
                            {renderAvatar(trade.senderUsername, trade.senderAvatar)}
                            <span className="trades-username">{trade.senderUsername}</span>
                          </div>
                        </td>
                        <td>
                          <Points points={trade.totalValue} type="small" />
                        </td>
                        <td>
                          <div className="trades-user">
                            {renderAvatar(trade.receiverUsername, trade.receiverAvatar)}
                            <span className="trades-username">{trade.receiverUsername}</span>
                          </div>
                        </td>
                        <td className="trades-date">{formatDate(trade.initiatedAt)}</td>
                        <td className="trades-date">{formatDate(trade.shippedAt)}</td>
                        <td className="trades-date">{formatDate(trade.completedAt)}</td>
                        <td>
                          <span
                            className="trades-status-badge"
                            style={{
                              backgroundColor: getStatusColor(trade.status),
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#fff'
                            }}
                          >
                            {formatStatus(trade.status)}
                          </span>
                        </td>
                      </tr>
                      {isExpanded && (
                        <AdminTradeExpandedRow
                          trade={trade}
                          onEditItem={handleEditItem}
                          onRemoveItem={handleRemoveItem}
                          onCompleteItem={handleCompleteItem}
                          completingItemId={completingItemId}
                        />
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {data && data.totalCount > 0 && (
          <div className="trades-pagination">
            <span className="trades-pagination-info">
              Showing {trades.length} of {data.totalCount} trade{data.totalCount !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {editingItem && (
        <AdminEditTradeItemModal
          trade={editingItem.trade}
          item={editingItem.item}
          onClose={() => setEditingItem(null)}
        />
      )}

      {removingItem && (
        <AdminRemoveCardConfirmModal
          trade={removingItem.trade}
          item={removingItem.item}
          onClose={() => setRemovingItem(null)}
        />
      )}
    </AdminLayout>
  );
};

export default TradesPage;
